import { useMutation, useQueryClient } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { Connector } from '@/types/core/connector';
import type { UUID } from '@/utils/uuid';
import { pollUntilResourceNewerThan } from '@/utils/poll';

import { connectorMutationKeys, connectorQueryKeys } from './query-keys';

/**
 * Hook to update a connector.
 * Returns a react-query mutation that accepts partial connector updates.
 * Polls until the read model reflects the change via _rev, then invalidates caches.
 */
export function useUpdateConnector(tenantId?: UUID, connectorId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: connectorMutationKeys.updateConnector(tenantId, connectorId),
    mutationFn: async (updates: {
      name?: string;
      description?: string;
      requirements?: Array<string>;
      requiredSites?: Array<string>;
    }) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!connectorId) throw new Error('Missing connector id');

      const cachedConnector = qc.getQueryData<Connector | null>(
        connectorQueryKeys.detail(tenantId, connectorId)
      );
      const minRev = cachedConnector?._rev ?? -2;

      await connectorService.updateConnector(tenantId, connectorId, updates);

      return pollUntilResourceNewerThan(
        () => connectorService.findConnector(tenantId, connectorId),
        minRev,
        { maxAttempts: 10, delayMs: 300 }
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: connectorQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
